import React from "react";
import { useHistory, useLocation } from "react-router";

import { faTimes } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faSearch, faSlidersH } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import cx from "classnames";
import { capitalize } from "lodash";

import { useDebouncedValue } from "~/lib/hooks";
import { inCalendarMode, isMobile } from "~/lib/utils";
import AskFellowButton from "~/nav/panels/AskFellow/AskFellowButton";
import { useClearSearchParam } from "~/search/filters/Filter";
import { useTrackersFeatureFlagOn } from "~/trackers/featureFlag";
import { Button, Tooltip } from "~/ui";
import { IconButton } from "~/ui/legacyFellowIndex";
import Menu from "~/ui/menu";

import { CompanyPanelToggleButton } from "../CompanyPanel/CompanyPanel";
import { CompanyPanelContext } from "../CompanyPanel/CompanyPanelContext";
import AttendeesFilter, {
	AttendeeFilterTriggerContent,
	PARAM_NAME as WITH_EMAIL_PARAM_NAME,
} from "../Filters/AttendeeFilter";
import CompanyFilter, { PARAM_NAME as COMPANY_PARAM_NAME } from "./CompanyFilter";
import PostTypeFilter, { type PostType, TYPE_TO_WORD, usePostTypeFilterQueryParam } from "./PostTypeFilter";
import SingleOptionFilter from "./SingleOptionFilter";
import TrackerFilter, { PARAM_NAME as TRACKER_PARAM_NAME, TrackerFilterTriggerContent } from "./TrackerFilter";

import type { MeetingType } from "~/__generated__/LibraryChannelPagePostsQuery.graphql";

import styles from "./Filters.module.css";

export const FilterButton = React.forwardRef<
	HTMLButtonElement,
	React.PropsWithChildren<{ onClear?: () => void | undefined; disabled?: boolean }>
>(function FilterButton({ children, onClear, disabled }, ref) {
	return (
		<Button
			ref={ref}
			variant="secondary"
			size="slim"
			className={styles.clearFilterButton}
			onClick={onClear}
			disabled={disabled}
		>
			{children}
			{onClear && (
				<>
					&ensp;
					<FontAwesomeIcon icon={faTimes} />
				</>
			)}
		</Button>
	);
});

const PARTICIPATION_PARAM_NAME = "meetingType";
const ATTENDEE_TYPE_PARAM_NAME = "attendeeType";

const DATE_PARAM_NAME = "date";
const SEARCH_PARAM_NAME = "search";

const PARTICIPATION_OPTIONS: { [key in MeetingType]: string } = {
	organized: "Meetings I organized",
	missed: "Meetings I didn't attend",
	// eslint-disable-next-line camelcase
	not_fully_attended: "Meetings I partially attended",
} as const;

const DATE_RANGE_OPTIONS = {
	today: "Today",
	yesterday: "Yesterday",
	lastSevenDays: "Last 7 days",
	lastThirtyDays: "Last 30 days",
	lastSixMonths: "Last 6 months",
	lastYear: "Last year",
} as const;
type DateRangeOption = keyof typeof DATE_RANGE_OPTIONS;

export const useCompanyFilterFlagOn = () => {
	return useFeatureIsOn("filter-by-company");
};

export const useLibraryQueryParams = () => {
	const location = useLocation();

	const clearParticipation = useClearSearchParam(PARTICIPATION_PARAM_NAME);
	const participation = React.useMemo(() => {
		const search = new URLSearchParams(location.search);
		const participationOptionFromUrl = search.get(PARTICIPATION_PARAM_NAME);

		let value: MeetingType | null = null;
		if (participationOptionFromUrl === "organized") {
			value = "organized";
		} else if (participationOptionFromUrl === "missed") {
			value = "missed";
		} else if (participationOptionFromUrl === "not_fully_attended") {
			value = "not_fully_attended";
		}

		if (!value) return null;
		return {
			value,
			clear: clearParticipation,
		};
	}, [location, clearParticipation]);

	const clearAttendeeEmails = useClearSearchParam(WITH_EMAIL_PARAM_NAME, [
		WITH_EMAIL_PARAM_NAME,
		ATTENDEE_TYPE_PARAM_NAME,
	]);
	const attendeeEmails = React.useMemo(() => {
		const search = new URLSearchParams(location.search);
		const value = search.get(WITH_EMAIL_PARAM_NAME)?.split(",");

		if (!value) return null;
		return {
			value,
			clear: clearAttendeeEmails,
		};
	}, [location, clearAttendeeEmails]);

	const attendeeType = React.useMemo(() => {
		const search = new URLSearchParams(location.search);
		const value = search.get(ATTENDEE_TYPE_PARAM_NAME);

		if (!value) return null;
		return {
			value,
			clear: clearAttendeeEmails,
		};
	}, [clearAttendeeEmails, location]);

	const clearCompany = useClearSearchParam(COMPANY_PARAM_NAME);
	const companyDomain = React.useMemo(() => {
		const search = new URLSearchParams(location.search);
		const value = search.get(COMPANY_PARAM_NAME);

		if (!value) return null;
		return {
			value,
			clear: clearCompany,
		};
	}, [clearCompany, location.search]);

	const clearDate = useClearSearchParam(DATE_PARAM_NAME);
	const date = React.useMemo(() => {
		const search = new URLSearchParams(location.search);
		const value = search.get(DATE_PARAM_NAME) ?? undefined;

		if (!value || !Object.keys(DATE_RANGE_OPTIONS).includes(value)) return null;

		const dateValue = value as DateRangeOption;

		return {
			value: dateValue,
			clear: clearDate,
		};
	}, [location, clearDate]);

	const [selectedPostTypes, changeSelectedPostTypes] = usePostTypeFilterQueryParam();
	const clearPostTypeFn = React.useCallback(
		(type: PostType) => () => changeSelectedPostTypes("REMOVE", type),
		[changeSelectedPostTypes],
	);
	const postTypes = React.useMemo(() => {
		if (selectedPostTypes.size === 0) return null;
		return {
			value: selectedPostTypes,
			clear: clearPostTypeFn,
		};
	}, [selectedPostTypes, clearPostTypeFn]);

	const clearTrackers = useClearSearchParam(TRACKER_PARAM_NAME);
	const trackers = React.useMemo(() => {
		const search = new URLSearchParams(location.search);
		const value = search.get(TRACKER_PARAM_NAME)?.split(",");

		if (!value) return null;
		return {
			value,
			clear: clearTrackers,
		};
	}, [clearTrackers, location]);

	const clearSearch = useClearSearchParam(SEARCH_PARAM_NAME);
	const search = React.useMemo(() => {
		const search = new URLSearchParams(location.search);
		const value = search.get(SEARCH_PARAM_NAME) ?? "";

		if (!value) return null;
		return {
			value,
			clear: clearSearch,
		};
	}, [location, clearSearch]);

	const debouncedSearchValue = useDebouncedValue(search?.value, 300);
	const debouncedSearch = React.useMemo(() => {
		if (!debouncedSearchValue) return null;
		return {
			value: debouncedSearchValue,
			clear: clearSearch,
		};
	}, [debouncedSearchValue, clearSearch]);

	const hasFilters = React.useMemo(() => {
		return (
			!!participation ||
			!!attendeeEmails ||
			!!date ||
			!!postTypes ||
			!!trackers ||
			!!debouncedSearch ||
			!!attendeeType ||
			!!companyDomain
		);
	}, [participation, attendeeEmails, date, postTypes, trackers, debouncedSearch, attendeeType, companyDomain]);

	return {
		participation,
		attendeeEmails,
		attendeeType,
		date,
		postTypes,
		trackers,
		search,
		debouncedSearch,
		hasFilters,
		companyDomain,
	};
};

interface SearchInputProps {
	searchPlaceholder: string;
	disabled?: boolean;
}

export function SearchInput({ searchPlaceholder, disabled }: SearchInputProps) {
	const { search } = useLibraryQueryParams();

	const history = useHistory();
	const location = useLocation();

	const handleSearchChange = React.useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const searchParams = new URLSearchParams(location.search);
			if (e.target.value) {
				searchParams.set(SEARCH_PARAM_NAME, e.target.value);
			} else {
				searchParams.delete(SEARCH_PARAM_NAME);
			}
			history.replace({
				pathname: location.pathname,
				search: searchParams.toString(),
			});
		},
		[history, location],
	);

	return (
		<label className={styles.searchInput}>
			<FontAwesomeIcon fixedWidth icon={faSearch} className={styles.searchIcon} />
			<input
				type="text"
				disabled={disabled}
				value={search?.value || ""}
				placeholder={searchPlaceholder}
				onChange={handleSearchChange}
			/>
			<IconButton
				icon={faTimes}
				className={cx(styles.clearSearchIcon, { [styles.enabled]: Boolean(search) })}
				onClick={search?.clear}
				disabled={!search}
			/>
		</label>
	);
}

export default function LibraryFilters({
	searchPlaceholder,
	disabled,
	hideAskFellowButton,
}: {
	searchPlaceholder: string;
	disabled?: boolean;
	hideAskFellowButton?: boolean;
}) {
	const { participation, attendeeEmails, attendeeType, date, postTypes, trackers, companyDomain } =
		useLibraryQueryParams();

	const isTrackersFeatureEnabled = useTrackersFeatureFlagOn();
	const isCompanyFilterEnabled = useCompanyFilterFlagOn();

	const onClearFitlerAttendeeEmails = React.useCallback(() => {
		attendeeEmails?.clear();
		attendeeType?.clear();
	}, [attendeeEmails, attendeeType]);

	const { isPanelVisible, setIsPanelVisible, companyPanelShouldMount } = React.useContext(CompanyPanelContext);
	const onOpenSidePanel = React.useCallback(() => {
		setIsPanelVisible(true);
	}, [setIsPanelVisible]);

	const showAskFellowButton = !hideAskFellowButton;
	const showCompanyPanelToggle =
		companyPanelShouldMount && !isMobile && !inCalendarMode && !isPanelVisible && onOpenSidePanel;

	return (
		<div className={styles.filtersAndSearch}>
			<div className={styles.filters}>
				<Menu>
					<Menu.Trigger asChild>
						<Button variant="secondary" size="slim" className={styles.filtersTrigger}>
							<FontAwesomeIcon icon={faSlidersH} />
							&ensp;Filters
						</Button>
					</Menu.Trigger>
					<Menu.Content>
						<SingleOptionFilter
							label="Participation"
							paramName={PARTICIPATION_PARAM_NAME}
							options={PARTICIPATION_OPTIONS}
							value={participation?.value || null}
						/>
						<AttendeesFilter attendeeType={attendeeType?.value} />
						{isCompanyFilterEnabled && <CompanyFilter />}
						<SingleOptionFilter
							label="Date"
							paramName={DATE_PARAM_NAME}
							options={DATE_RANGE_OPTIONS}
							value={date?.value || null}
						/>
						<PostTypeFilter />
						{isTrackersFeatureEnabled && <TrackerFilter />}
					</Menu.Content>
				</Menu>
				{participation && (
					<Tooltip content="Excludes notes" placement="top">
						<FilterButton onClear={participation.clear}>{PARTICIPATION_OPTIONS[participation.value]}</FilterButton>
					</Tooltip>
				)}
				{(attendeeEmails || attendeeType) && (
					<FilterButton onClear={onClearFitlerAttendeeEmails}>
						<AttendeeFilterTriggerContent
							selectedUsers={attendeeEmails?.value?.map(email => ({ email, fullName: null, image: null })) ?? []}
							isExternalUsers={attendeeType?.value === "external"}
						/>
					</FilterButton>
				)}
				{companyDomain && isCompanyFilterEnabled && (
					<FilterButton onClear={companyDomain.clear}>{companyDomain.value}</FilterButton>
				)}
				{trackers && isTrackersFeatureEnabled && (
					<FilterButton onClear={trackers.clear}>
						<TrackerFilterTriggerContent selectedTrackerIds={trackers.value} />
					</FilterButton>
				)}
				{date && <FilterButton onClear={date.clear}>{DATE_RANGE_OPTIONS[date.value]}</FilterButton>}
				{postTypes &&
					Array.from(postTypes.value).map(type => (
						<FilterButton key={type} onClear={postTypes.clear(type)}>
							{capitalize(TYPE_TO_WORD[type])}
						</FilterButton>
					))}
			</div>

			<div className={styles.searchWrapper}>
				{showAskFellowButton && <AskFellowButton source="channel_page" />}
				<SearchInput searchPlaceholder={searchPlaceholder} disabled={disabled} />
				{showCompanyPanelToggle && <CompanyPanelToggleButton onClick={onOpenSidePanel} />}
			</div>
		</div>
	);
}

export const FilterEmptySearchResult = ({ objectName }: { objectName: string }) => {
	return (
		<div className={styles.emptySearchResult}>
			No {objectName} found.
			<br />
			Try adjusting your search.
		</div>
	);
};
